import { EventSource } from 'eventsource';
import { EVENT_TYPES } from '../constants/constants';
import { makeServerSideEventSubscriptions } from './makeServerSideEventSubscriptions';
import { ServerSideEventError } from './ServerSideEventError';
import { transformServerSideEventMessage } from './transformServerSideEventMessage';
import type { ServerSideEventSubscribe } from './types';

export type ServerSideEventConnection = {
    connect: () => void;
    disconnect: () => void;
    getEventSource: () => EventSource | null;
    subscribe: ServerSideEventSubscribe;
};

export type ServerSideEventConnectionOptions = {
    ownerId: string | undefined;
    authenticated: boolean;
    readToken: () => string | undefined;
    reportError: (error: ServerSideEventError) => void;
    requestMode?: 'same-origin' | 'no-cors';
};

/** Integration boundary: owns the stream, reconnect timers, and subscriptions for one authenticated scope. */
export const makeServerSideEventConnection = ({
    ownerId,
    authenticated,
    readToken,
    reportError,
    requestMode = 'same-origin'
}: ServerSideEventConnectionOptions): ServerSideEventConnection => {
    const subscriptions = makeServerSideEventSubscriptions(reportError);
    let activeSource: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryTime = 5000;
    let disposed = true;

    const openConnection = () => {
        if (disposed || !ownerId || !authenticated) return;
        let source: EventSource;
        try {
            const token = readToken();
            source = new EventSource('/events/v1/stream', {
                fetch: (input, init) => {
                    const options = {
                        ...init,
                        headers: {
                            ...init.headers,
                            Authorization: token ? `Bearer ${token}` : '',
                            OwnerId: ownerId,
                            mode: requestMode
                        }
                    };
                    const response = fetch(input, options);
                    return response;
                }
            });
        } catch (cause) {
            const error = new ServerSideEventError('connection', cause);
            reportError(error);
            return;
        }
        activeSource = source;
        const isCurrentConnection = () => {
            const current = !disposed && activeSource === source;
            return current;
        };
        const receiveMessage = (message: MessageEvent<string>, named = false) => {
            if (!isCurrentConnection()) return;
            try {
                const event = transformServerSideEventMessage(message, named);
                if (event) subscriptions.publish(event);
            } catch (cause) {
                const error = cause instanceof ServerSideEventError ? cause : new ServerSideEventError('parse', cause);
                reportError(error);
            }
        };
        Object.values(EVENT_TYPES).forEach((listenerType) => {
            source.addEventListener(listenerType, (message: MessageEvent<string>) => {
                receiveMessage(message, true);
            });
        });
        source.onmessage = (message) => {
            receiveMessage(message);
        };
        source.onerror = (error) => {
            if (!isCurrentConnection()) return;
            activeSource = null;
            source.close();
            // Transport failures and stream closures have no HTTP status; their messages vary by runtime.
            const isRetryableError = error.code === undefined || [500, 502, 503, 504].includes(error.code);
            if (isRetryableError) {
                retryTime = Math.min(retryTime * 2, 30000);
                clearTimeout(retryTimer);
                const reconnect = () => {
                    retryTimer = undefined;
                    openConnection();
                };
                retryTimer = setTimeout(reconnect, retryTime);
            }
            const connectionError = new ServerSideEventError('connection', error);
            reportError(connectionError);
        };
    };
    const connect = () => {
        if (activeSource || retryTimer !== undefined) return;
        disposed = false;
        retryTime = 5000;
        openConnection();
    };
    const disconnect = () => {
        disposed = true;
        clearTimeout(retryTimer);
        retryTimer = undefined;
        activeSource?.close();
        activeSource = null;
        subscriptions.clear();
    };
    const getEventSource = () => {
        return activeSource;
    };
    const connection = { connect, disconnect, getEventSource, subscribe: subscriptions.subscribe };
    return connection;
};
